import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Trash2, Save, Loader2, Pencil, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function MyPlansDialog({ open, onOpenChange, currentPlan, onLoadPlan }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState(null); // { id, name }
  const [renaming, setRenaming] = useState(false);

  const handleRename = async () => {
    const name = editing?.name.trim();
    if (!name) { toast.error("Enter a name"); return; }
    setRenaming(true);
    try {
      const { data } = await api.patch(`/plans/${editing.id}/rename`, { name });
      setPlans((prev) => prev.map((p) => (p.id === data.id ? { ...p, name: data.name, updated_at: data.updated_at } : p)));
      toast.success("Renamed");
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRenaming(false); }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plans");
      setPlans(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const handleSave = async () => {
    if (!saveName.trim()) { toast.error("Enter a name"); return; }
    if (!currentPlan || !currentPlan.polygon || currentPlan.polygon.length < 3) {
      toast.error("Draw a polygon before saving");
      return;
    }
    setSaving(true);
    try {
      await api.post("/plans", { ...currentPlan, name: saveName.trim() });
      toast.success("Plan saved");
      setSaveName("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const handleLoad = (plan) => {
    onLoadPlan(plan);
    onOpenChange(false);
    toast.success(`Loaded "${plan.name}"`);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/plans/${id}`);
      setPlans((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDeletingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl" data-testid="my-plans-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-amber-500" />
            My Plans
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Save the current sector configuration or load a previously saved plan.
          </DialogDescription>
        </DialogHeader>

        {/* Save current */}
        <div className="mt-2 p-4 rounded-lg border border-slate-800 bg-slate-950/60">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Save current</div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="plan-name" className="text-xs text-slate-400">Plan name</Label>
              <Input
                id="plan-name"
                data-testid="save-plan-name-input"
                value={saveName} onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. 3BHK Main House"
                className="bg-slate-900 border-slate-800 text-slate-100 h-10"
              />
            </div>
            <Button
              onClick={handleSave} disabled={saving}
              data-testid="save-plan-button"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-10"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save</>}
            </Button>
          </div>
          <div className="mt-2 font-mono text-[10px] text-slate-500">
            {currentPlan?.polygon?.length || 0} corners · {currentPlan?.sector_count || 16} sectors · rotation {(currentPlan?.rotation || 0).toFixed(1)}°
          </div>
        </div>

        {/* List */}
        <div className="mt-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Saved plans</div>
          <div className="rounded-lg border border-slate-800 bg-slate-950/40 max-h-72 overflow-y-auto" data-testid="plans-list">
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-slate-400 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : plans.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No saved plans yet</div>
            ) : (
              plans.map((p) => (
                <div
                  key={p.id}
                  data-testid={`plan-row-${p.id}`}
                  className="flex items-center justify-between px-3 py-2.5 border-b border-slate-900 last:border-0 hover:bg-slate-900/60 transition-colors"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    {editing?.id === p.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          autoFocus
                          data-testid={`rename-plan-input-${p.id}`}
                          value={editing.name}
                          onChange={(e) => setEditing({ id: p.id, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setEditing(null); }}
                          className="bg-slate-950 border-amber-500/60 text-slate-100 h-8 text-sm"
                        />
                        <button type="button" data-testid={`rename-plan-save-${p.id}`} onClick={handleRename} disabled={renaming}
                          className="h-8 w-8 flex items-center justify-center rounded text-emerald-400 hover:bg-emerald-950/40">
                          {renaming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button type="button" data-testid={`rename-plan-cancel-${p.id}`} onClick={() => setEditing(null)}
                          className="h-8 w-8 flex items-center justify-center rounded text-slate-400 hover:bg-slate-800">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 group">
                        <div className="font-display text-sm text-slate-100 truncate" data-testid={`plan-name-${p.id}`}>{p.name}</div>
                        <button type="button" data-testid={`rename-plan-${p.id}`} onClick={() => setEditing({ id: p.id, name: p.name })}
                          className="text-slate-500 hover:text-amber-400 opacity-60 group-hover:opacity-100 transition-opacity" title="Rename">
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                      {p.polygon.length} corners · {p.sector_count} sectors · {(p.markers || []).length} rooms · {new Date(p.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      data-testid={`load-plan-${p.id}`}
                      onClick={() => handleLoad(p)}
                      className="h-8 bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100"
                    >
                      Load
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      data-testid={`delete-plan-${p.id}`}
                      onClick={() => handleDelete(p.id)}
                      disabled={deletingId === p.id}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-400 hover:bg-red-950/40"
                    >
                      {deletingId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
